import { action, observable, computed, reaction } from 'mobx'
import constants from '@app/constants'
import { Track } from '@app/models/Track'
import { Color } from '@app/models/Color'
import { min, quantile } from 'simple-statistics'
import { clamp } from 'lodash'
import { formatLapPrecise } from '@app/views/utils'

const LAP_GAP = 5

const millisecondsFromMMSS = (s: string) => {
  if (s === null) {
    return null
  } else {
    const [minutes, seconds] = s.split(':')
    return (parseInt(minutes, 10) * 60 + parseFloat(seconds)) * 1000
  }
}

export class RootStore {
  @observable initialized = false
  @observable maxLap: number
  @observable hoveredDataset: any = null
  @observable hoveredDriver: number = null
  @observable hoveredLap: number = null
  @observable selectedLap: number = null
  @observable selectedDataset: any = null
  @observable selectedDriver: number = null
  @observable selectedYear: number
  @observable minYear: number
  @observable maxYear: number
  @observable width = 100
  @observable height = 100
  tracks = observable([])
  @observable selectedTrack: Track
  @observable trackSelectOpen = false
  @observable helpOpen = false

  lapData: any
  pitStopData: any
  qualifyingData: any
  driverData: any
  lapNames: any
  lapTicks: any
  qualifyingNames = ['Session 1', 'Session 2', 'Session 3']
  activeColor = new Color('hsl(42, 74%, 49%)')
  previousColor = new Color('hsl(42, 74%, 70%)')
  defaultColor = new Color('rgba(0, 0, 68, 0.27)')

  async initialize() {
    this.tracks.replace(constants.TRACKS.map(track => new Track(track)))

    reaction(
      () => ({ track: this.selectedTrack }),
      () => {
        this.loadTrack()
      }
    )

    this.selectedTrack = this.tracks[0]
  }

  async loadTrack() {
    this.initialized = false

    const responses = await Promise.all([
      this.fetchJSON('lapTimes'),
      this.fetchJSON('pitStopTimes'),
      this.fetchJSON('qualifyingTimes'),
      this.fetchJSON('driverList'),
    ])

    this.lapData = responses[0]
    this.pitStopData = responses[1]
    this.qualifyingData = responses[2]
    this.driverData = responses[3]

    this.setupTrack()
    this.initialized = true
  }

  @action setupTrack() {
    const years = this.lapData.map((lap: any) => lap.year)
    this.minYear = Math.min(...years)
    this.maxYear = Math.max(...years)
    this.selectedYear = this.maxYear
    this.selectedDriver = null
    this.selectedDataset = null
    this.selectedLap = null
    this.hoveredLap = null

    const laps = this.lapData.map((lap: any) => lap.lap)
    this.maxLap = Math.max(...laps)

    const names = new Array(this.maxLap).fill(true)
    this.lapNames = names.map((item, i) => `Lap ${i + 1}`)

    const ticks = new Array(Math.floor(this.maxLap / LAP_GAP)).fill(true)
    this.lapTicks = [
      'Lap 1',
      ...ticks.map((item, i) => `Lap ${(i + 1) * LAP_GAP}`),
    ]
  }

  async fetchJSON(url: string) {
    const response = await fetch(
      constants.TRACK_URL(this.selectedTrack.id, url)
    )

    return await response.json()
  }

  driverSort(driver: any) {
    if (driver.driverHovered) {
      return 2
    } else if (driver.driverSelected) {
      return 1
    } else {
      return 0
    }
  }

  sortBySelectedDriver(a: any, b: any) {
    return this.driverSort(a) - this.driverSort(b)
  }

  sortByTime(a: any, b: any) {
    return a.time - b.time
  }

  isActiveDriver(driverId: number) {
    return (
      this.hoveredDriver === driverId ||
      (this.hoveredDriver === null && this.selectedDriver === driverId)
    )
  }

  pointColor(driverId: number) {
    if (this.isActiveDriver(driverId)) {
      return this.activeColor
    } else if (this.selectedDriver === driverId) {
      return this.previousColor
    } else {
      return this.defaultColor
    }
  }

  pointOutline(driverId: number) {
    if (this.isActiveDriver(driverId)) {
      return 1
    } else if (this.selectedDriver === driverId) {
      return 1
    } else {
      return 0
    }
  }

  @computed get lapTimes() {
    return this.lapData
      .filter(({ year }: { year: number }) => year === this.selectedYear)
      .map((time: any) => ({
        ...time,
        clampedTime: clamp(time.time, this.yDomain[0], this.yDomain[1]),
        driverHovered: this.hoveredDriver === time.driverId,
        driverSelected: this.selectedDriver === time.driverId,
        color: this.pointColor(time.driverId),
        outline: this.pointOutline(time.driverId),
        formattedTime: formatLapPrecise(time.time),
      }))
      .sort(this.sortBySelectedDriver.bind(this))
  }

  @computed get pitStopTimes() {
    return this.pitStopData
      .filter(({ year }: { year: number }) => year === this.selectedYear)
      .map((time: any) => ({
        ...time,
        clampedTime: clamp(
          time.time,
          this.pitStopDomain[0],
          this.pitStopDomain[1]
        ),
        driverHovered: this.hoveredDriver === time.driverId,
        driverSelected: this.selectedDriver === time.driverId,
        color: this.pointColor(time.driverId),
        outline: this.pointOutline(time.driverId),
        formattedTime: formatLapPrecise(time.time),
      }))
      .sort(this.sortBySelectedDriver.bind(this))
  }

  @computed get allQualifyingTimes() {
    const times: any = []

    const addQualifyingTime = (
      time: any,
      session: string,
      property: string
    ) => {
      const millis = millisecondsFromMMSS(time[property])
      if (millis !== null && !isNaN(millis)) {
        times.push({
          ...time,
          session,
          time: millis,
        })
      }
    }

    this.qualifyingData.forEach((time: any) => {
      addQualifyingTime(time, '1', 'q1')
      addQualifyingTime(time, '2', 'q2')
      addQualifyingTime(time, '3', 'q3')
    })

    return times
  }

  @computed get qualifyingTimes() {
    return this.allQualifyingTimes
      .filter(({ year }: { year: number }) => year === this.selectedYear)
      .map((time: any) => ({
        ...time,
        clampedTime: clamp(time.time, this.yDomain[0], this.yDomain[1]),
        driverHovered: this.hoveredDriver === time.driverId,
        driverSelected: this.selectedDriver === time.driverId,
        color: this.pointColor(time.driverId),
        outline: this.pointOutline(time.driverId),
        formattedTime: formatLapPrecise(time.time),
      }))
      .sort(this.sortBySelectedDriver.bind(this))
  }

  @computed get driverList() {
    return this.driverData
      .filter(({ year }: { year: number }) => year === this.selectedYear)
      .map((driver: any) => ({
        ...driver,
        driverHovered: this.hoveredDriver === driver.driverId,
        driverSelected: this.selectedDriver === driver.driverId,
        lapsBehind: this.findLapsBehind(driver),
        formattedTime: driver.time,
      }))
  }

  findLapsBehind(driver: any) {
    const statusNumber = parseInt(driver.statusId, 10)
    if (!isNaN(statusNumber) && statusNumber > 10 && statusNumber < 20) {
      return statusNumber - 10
    }
  }

  @computed get timeDisplayTitle() {
    const dataset = this.hoveredDataset || this.selectedDataset
    const lap = this.hoveredLap || this.selectedLap
    if (dataset === 'lapTimes') {
      return `Lap ${lap + 1} - Lap Times`
    } else if (dataset === 'pitStopTimes') {
      return `Lap ${lap + 1} - Pitstop Times`
    } else if (dataset === 'qualifyingTimes') {
      return `Session ${lap + 1} - Qualifying Times`
    } else {
      return null
    }
  }

  @computed get timeDisplayList() {
    const dataset = this.hoveredDataset || this.selectedDataset
    const lap = this.hoveredLap || this.selectedLap
    if (dataset === 'lapTimes') {
      return this.lapTimes
        .filter(
          ({ lapName }: { lapName: string }) => lapName === `Lap ${lap + 1}`
        )
        .sort(this.sortByTime)
    } else if (dataset === 'pitStopTimes') {
      return this.pitStopTimes
        .filter(
          ({ lapName }: { lapName: string }) => lapName === `Lap ${lap + 1}`
        )
        .sort(this.sortByTime)
    } else if (dataset === 'qualifyingTimes') {
      return this.qualifyingTimes
        .filter(({ session }: { session: string }) => session === `${lap + 1}`)
        .sort(this.sortByTime)
    } else {
      return this.driverList
    }
  }

  @computed get yDomain() {
    const times = this.lapData
      .concat(this.allQualifyingTimes)
      .map((time: any) => time.time)

    const minimum = min(times)
    const upper = quantile(times, 0.9)
    const maximum = minimum + (upper - minimum) * 1.5
    const roundTo = 5000
    return [
      Math.floor(minimum / roundTo) * roundTo,
      Math.ceil(maximum / roundTo) * roundTo,
    ]
  }

  @computed get qualifyingDomain() {
    return this.yDomain
  }

  @computed get lapTimesDomain() {
    return this.yDomain
  }

  @computed get pitStopDomain() {
    const times = this.pitStopData.map((time: any) => time.time)

    const minimum = min(times)
    const upper = Math.min(quantile(times, 0.9), 60000)
    const maximum = upper
    const roundTo = 5000
    return [
      Math.floor(minimum / roundTo) * roundTo,
      Math.ceil(maximum / roundTo) * roundTo,
    ]
  }
}
